import React, { useEffect, useState } from "react";
import {
  Col,
  Container,
  Row,
  Modal,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  GET_CATEGORY_REQUEST,
  GETVENDORTHERAPIST,
  ACCEPTCATEGORY,
  REASSIGNCATEGORY,
} from "../../../../services/ApiCalls";
import RejectCategoryPop from "../../../../components/Modals/RejectedCategoryPop";

const HelpDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // States
  const [disputeDetails, setDisputeDetails] = useState(null);
  const [loading, setLoading] = useState(true); // Spinner for main content
  const [vendorLoading, setVendorLoading] = useState(false); // Spinner for vendor modal
  const [rejectModal, setRejectModal] = useState({
    show: false,
    categoryId: null,
  });
  const [disableReassignButton, setDisableReassignButton] = useState(false);
  const [vendorModal, setVendorModal] = useState({
    show: false,
    category: null,
    vendors: [],
  });
  const [reassignVendorModal, setReassignVendorModal] = useState({
    show: false,
    category: null,
    vendors: [],
  });
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedReassignVendors, setSelectedReassignVendors] = useState([]);
  // Fetch dispute details
  const getDisputeDetailsData = async () => {
    setDisableReassignButton(true);

    setLoading(true);
    try {
      const response = await GET_CATEGORY_REQUEST({ _id: id });
      if (response?.data?.status === "success") {
        setDisputeDetails(response.data.data);
        setSelectedReassignVendors(response.data.data?.vendorIds.map(vendor => vendor._id) || []);
      } else {
        toast.error(response?.data?.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching dispute details:", error);
      toast.error("Something went wrong while fetching data.");
    } finally {
      setLoading(false);
      setDisableReassignButton(false);
    }
  };

  useEffect(() => {
    getDisputeDetailsData();
  }, [id]);

  // Open vendor modal and fetch therapists
  const openVendorModal = async (categoryItem) => {
    setLoading(true);
    try {
      const res = await GETVENDORTHERAPIST({
        category: categoryItem.categoryId._id,
        limit: 1000, // el backend devuelve 10 por defecto: traer todos los terapeutas
      });
      if (res?.data?.status === "success") {
        setVendorModal({
          show: true,
          category: categoryItem,
          vendors: res.data?.data || [],
        });
        setLoading(false);
      } else {
        toast.error(res?.data?.message || "No therapists found.");
      }
    } catch (err) {
      console.error("Error fetching vendors", err);
      toast.error("Error fetching therapists.");
    } finally {
      setVendorLoading(false);
    }
  };
  const openReassignVendorModal = async (categoryItem) => {
    setLoading(true);
    try {
      const res = await GETVENDORTHERAPIST({
        category: categoryItem.categoryId._id,
        limit: 1000, // el backend devuelve 10 por defecto: traer todos los terapeutas
      });
      if (res?.data?.status === "success") {
        setReassignVendorModal({
          show: true,
          category: categoryItem,
          vendors: res.data?.data || [],
        });
        setLoading(false);
      } else {
        toast.error(res?.data?.message || "No therapists found.");
      }
    } catch (err) {
      console.error("Error fetching vendors", err);
      toast.error("Error fetching therapists.");
    } finally {
      setVendorLoading(false);
    }
  };

  // Select vendor (max 3)
  const handleVendorSelect = (vendorId) => {
    setSelectedVendors((prev) => {
      if (prev.includes(vendorId)) return prev.filter((id) => id !== vendorId);
      if (prev.length < 3) return [...prev, vendorId];
      toast.warning("You can select up to 3 vendors only.");
      return prev;
    });
  };

  const handleReassignVendorSelect = (vendorId) => {
    setSelectedReassignVendors((prev) => {
      if (prev.includes(vendorId)) return prev.filter((id) => id !== vendorId);
      if (prev.length < 3) return [...prev, vendorId];
      toast.warning("You can select up to 3 vendors only.");
      return prev;
    });
  };
  // Accept category
  const handleAcceptCategory = async () => {
    setLoading(true);
    try {
      const body = {
        _id: vendorModal?.category?._id,
        vendorIds: selectedVendors,
      };
      const res = await ACCEPTCATEGORY(body);
      if (res?.data?.status === "success") {
        toast.success("Category accepted successfully!");
        getDisputeDetailsData(); // Refresh data
        setLoading(false);
        setVendorModal({ show: false, category: null, vendors: [] });
        setSelectedVendors([]);
      } else {
        toast.error(res?.data?.message || "Something went wrong!");
      }
    } catch (err) {
      console.error("Error accepting category", err);
      toast.error("Error accepting category.");
    }
  };
  const handleReassignCategory = async () => {
    setLoading(true);
    try {
      const body = {
        _id: reassignVendorModal?.category?._id,
        vendorIds: selectedReassignVendors,
        page:1,
        limit:1000,
      };
      const res = await REASSIGNCATEGORY(body);
      if (res?.data?.status === "success") {
        toast.success("Category reassigned successfully!");
        getDisputeDetailsData(); // Refresh data
        setLoading(false);
        setReassignVendorModal({ show: false, category: null, vendors: [] });
        setSelectedReassignVendors([]);
      } else {
        toast.error(res?.data?.message || "Something went wrong!");
      }
    } catch (err) {
      console.error("Error reassigning category", err);
      toast.error("Error reassigning category.");
    }
  };

  // Reject category placeholder
  const handleRejectCategory = () => {
    toast.info("Reject category handler not implemented yet.");
    setRejectModal({ show: false, categoryId: null });
  };

  return (
    <>
      {/* Reject Modal */}
      <RejectCategoryPop
        show={rejectModal.show}
        setShow={(value) => setRejectModal({ show: value, categoryId: null })}
        confirmHandler={handleRejectCategory}
      />

      {/* Vendor Modal */}
      <Modal
        show={vendorModal.show}
        onHide={() =>
          setVendorModal({ show: false, category: null, vendors: [] })
        }
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Therapist</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {vendorLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : vendorModal?.vendors?.length > 0 ? (
            vendorModal.vendors.map((vendor) => (
              <Form.Check
                key={vendor._id}
                type="checkbox"
                label={`${vendor.fname || ""} ${vendor.lname || ""} (${
                  vendor.email || ""
                })`}
                checked={selectedVendors.includes(vendor._id)}
                onChange={() => handleVendorSelect(vendor._id)}
              />
            ))
          ) : (
            <p>No Therapist found for this category.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setVendorModal({ show: false, category: null, vendors: [] })
            }
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAcceptCategory}
            disabled={loading ? true : false}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={reassignVendorModal.show}
        onHide={() =>
          setReassignVendorModal({ show: false, category: null, vendors: [] })
        }
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Reassign Therapist</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {vendorLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : reassignVendorModal?.vendors?.length > 0 ? (
            reassignVendorModal.vendors.map((vendor) => (
              <Form.Check
                key={vendor._id}
                type="checkbox"
                label={`${vendor.fname || ""} ${vendor.lname || ""} (${
                  vendor.email || ""
                })`}
                checked={selectedReassignVendors.includes(vendor._id)}
                onChange={() => handleReassignVendorSelect(vendor._id)}
              />
            ))
          ) : (
            <p>No Therapist found for this category.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setReassignVendorModal({ show: false, category: null, vendors: [] })
            }
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleReassignCategory}
            disabled={loading ? true : false}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Main Content */}
      <section className="helpDetail py-3 position-relative">
        <Container>
          {loading && (
            <div className="loaderspinnerwrap text-center py-5">
              <div className="loaderspinner">
                <Spinner animation="border" role="status"></Spinner>
              </div>
            </div>
          )}
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to="/help"
                  className="border d-flex align-items-center p-2 rounded"
                >
                  {/* SVG Back Arrow */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#1E232C"
                      stroke="#1E232C"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold themeBlue text-capitalize">
                  Therapist Assignment Detail
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <div className="formWrpper px-lg-5 p-md-4 p-3 rounded">
                <Row className="justify-content-between">
                  {/* Category Name */}
                  <Col
                    md={6}
                    className="list-unstyled ps-0 mb-0 notLastBorder pe-lg-3"
                  >
                    <li className="py-3 d-flex align-items-center gap-10">
                      <p className="m-0 themeBlue fw-sbold w-50">
                        Category Name:
                      </p>
                      <h6 className="m-0 text-muted fw-bold w-50">
                        {disputeDetails?.categoryId?.catName}
                      </h6>
                    </li>
                  </Col>

                  {/* User Name */}
                  <Col md={6}>
                    <li className="py-3 d-flex align-items-center gap-10">
                      <p className="m-0 themeBlue fw-sbold w-50">User Name:</p>
                      <h6 className="m-0 text-muted fw-bold w-50">
                        {`${disputeDetails?.userId?.fname || ""} ${
                          disputeDetails?.userId?.lname || ""
                        }`}
                      </h6>
                    </li>
                  </Col>

                  {/* Status */}
                  <Col md={6}>
                    <div className="py-3 d-flex align-items-center gap-10">
                      <p className="m-0 themeBlue fw-sbold w-50">Status</p>
                      <h6 className="m-0 text-muted fw-bold w-50">
                        {disputeDetails?.status}
                      </h6>
                    </div>
                  </Col>

                  {/* User Email */}
                  <Col md={6}>
                    <div className="py-3 d-flex align-items-center gap-10">
                      <p className="m-0 themeBlue fw-sbold w-50">User Email:</p>
                      <h6 className="m-0 text-muted fw-bold w-50">
                        {disputeDetails?.userId?.email}
                      </h6>
                    </div>
                  </Col>

                  {/* Questionnaire */}
                  <Col lg="12" className="my-4">
                    <div className="border-top py-3">
                      <h5 className="fw-bold themeBlue mb-3">Questionnaire</h5>
                      {disputeDetails?.submitions?.map((submission, index) => (
                        <div className="mb-4" key={submission._id}>
                          <h6 className="fw-bold mb-2">
                            {index + 1}. {submission.questionId?.name}
                          </h6>
                          <ul className="list-unstyled ps-3">
                            {submission?.values?.map((value, i) => (
                              <li key={i}>• {value}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </Col>

                  {/* Accept / Reject Buttons */}
                  <Col lg="12" className="d-flex align-items-center gap-2">
                  {
                    disputeDetails?.status?.toLowerCase() === "approved" && (
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => openReassignVendorModal(disputeDetails)}
                      >
                        Reassign Therapist
                      </button>
                    )
                  }
                    {disputeDetails?.status?.toLowerCase() === "pending" && (
                      <>
                        <button
                          className="btn btn-sm btn-success"
                          disabled={disableReassignButton}
                          onClick={() => openVendorModal(disputeDetails)}
                        >
                          Accept
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            setRejectModal({
                              show: true,
                              categoryId: disputeDetails?._id,
                            })
                          }
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default HelpDetail;
